import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import useStore from "../../../../store/useStore";
import usePincodeCheck from "../../../../utils/usePincodeCheck";
import { ADDR_REQUIRED, EMPTY_ADDR, validateAddr } from "../utils/addressValidation";
import PincodeStatus from "./PincodeStatus";

const AddressForm = ({ onSuccess, onCancel }) => {
  const { addAddress, addresses_loading } = useStore();
  const [addrForm, setAddrForm] = useState(EMPTY_ADDR);
  const [addrErrors, setAddrErrors] = useState({});
  const [addrTouched, setAddrTouched] = useState({});
  const [pincodeCityState, setPincodeCityState] = useState({ city: '', state: '' });

  const { status: pincodeStatus, message: pincodeMessage } = usePincodeCheck(addrForm.pincode);

  // Auto-fill city and state for specific pincodes
  useEffect(() => {
    const pincode = addrForm.pincode?.trim();
    if (pincode?.length === 6) {
      // Check for specific pincodes: 411033 and 411028
      if (pincode === '411033' || pincode === '411028') {
        setPincodeCityState({ city: 'Pune', state: 'Maharashtra' });
      } else {
        setPincodeCityState({ city: '', state: '' });
      }
    } else {
      setPincodeCityState({ city: '', state: '' });
    }
  }, [addrForm.pincode]);

  // Update form when pincodeCityState changes (but not when user is typing)
  useEffect(() => {
    if (pincodeCityState.city && pincodeCityState.state && !addrTouched.city && !addrTouched.state) {
      setAddrForm((prev) => ({
        ...prev,
        city: pincodeCityState.city,
        state: pincodeCityState.state,
      }));
    } else if (!addrForm.pincode || addrForm.pincode.length !== 6) {
      // Clear city and state when pincode is removed or invalid
      if (pincodeCityState.city === '' && pincodeCityState.state === '') {
        setAddrForm((prev) => ({
          ...prev,
          city: '',
          state: '',
        }));
      }
    }
  }, [pincodeCityState, addrForm.pincode]);

  const handleAddrChange = (e) => {
    const { name, value, type, checked } = e.target;
    let clean = value;
    if (name === "pincode") clean = value.replace(/\D/g, "").slice(0, 6);
    else if (name === "city" || name === "state")
      clean = value.replace(/[^a-zA-Z\s]/g, "");
    else if (name === "name") clean = value.replace(/[^a-zA-Z\s]/g, "");
    const final = type === "checkbox" ? checked : clean;
    
    // Clear pincodeCityState when user manually edits city/state
    if (name === "city" || name === "state") {
      setPincodeCityState({ city: '', state: '' });
    }
    
    setAddrForm((p) => ({ ...p, [name]: final }));
    if (addrTouched[name])
      setAddrErrors((p) => ({ ...p, [name]: validateAddr(name, final) }));
  };

  const handleAddrBlur = (e) => {
    const { name, value } = e.target;
    setAddrTouched((p) => ({ ...p, [name]: true }));
    setAddrErrors((p) => ({ ...p, [name]: validateAddr(name, value) }));
  };

  const handleSaveNewAddress = async (e) => {
    e.preventDefault();
    if (pincodeStatus === "not_serviceable") {
      toast.error("Cannot save — delivery not available for this pincode.");
      return;
    }
    if (pincodeStatus === "checking") {
      toast("Checking pincode, please wait…", { icon: "⏳" });
      return;
    }
    const newErrors = {};
    const newTouched = {};
    ADDR_REQUIRED.forEach((f) => {
      newTouched[f] = true;
      newErrors[f] = validateAddr(f, addrForm[f] ?? "");
    });
    setAddrTouched(newTouched);
    setAddrErrors(newErrors);
    if (Object.values(newErrors).some((err) => err)) return;

    try {
      const saved = await addAddress(addrForm);
      toast.success("Address saved!");
      setAddrForm(EMPTY_ADDR);
      setAddrErrors({});
      setAddrTouched({});
      if (onSuccess) onSuccess(saved);
    } catch (err) {
      toast.error(err.message || "Failed to save address");
    }
  };

  const addrInputClass = (name) =>
    `w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 ${
      addrErrors[name] && addrTouched[name]
        ? "border-red-400 focus:border-red-400 focus:ring-red-100"
        : "border-gray-200 focus:border-primary-500 focus:ring-primary-100"
    }`;

  return (
    <form
      onSubmit={handleSaveNewAddress}
      noValidate
      className="mb-6 space-y-3 rounded-2xl border border-dashed border-primary-200 bg-primary-50/30 p-4"
    >
      <p className="text-sm font-bold text-gray-700">New Address</p>

      {/* Name + Email */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
            Full Name *
          </label>
          <input
            name="name"
            value={addrForm.name}
            onChange={handleAddrChange}
            onBlur={handleAddrBlur}
            className={addrInputClass("name")}
            placeholder="Rahul Sharma"
            type="text"
            inputMode="text"
            autoComplete="name"
          />
          {addrErrors.name && addrTouched.name && (
            <p className="mt-1 text-xs text-red-500">{addrErrors.name}</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
            Email *
          </label>
          <input
            name="email"
            type="email"
            value={addrForm.email}
            onChange={handleAddrChange}
            onBlur={handleAddrBlur}
            className={addrInputClass("email")}
            placeholder="rahul@email.com"
            inputMode="email"
            autoComplete="email"
          />
          {addrErrors.email && addrTouched.email && (
            <p className="mt-1 text-xs text-red-500">{addrErrors.email}</p>
          )}
        </div>
      </div>

      {/* Address Type */}
      <div>
        <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
          Address Type
        </label>
        <div className="flex gap-2">
          {["Home", "Work", "Other"].map((t) => (
            <button
              key={t}
              type="button"
              onClick={() =>
                setAddrForm((p) => ({ ...p, address_type: t }))
              }
              className={`flex-1 rounded-xl border py-2 text-xs font-bold transition ${
                addrForm.address_type === t
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 text-gray-500 hover:border-primary-300"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
          Address
        </label>
        <textarea
          name="address"
          value={addrForm.address}
          onChange={handleAddrChange}
          onBlur={handleAddrBlur}
          rows={2}
          className={addrInputClass("address")}
          placeholder="123, MG Road, Near Bus Stand"
        />
        {addrErrors.address && addrTouched.address && (
          <p className="mt-1 text-xs text-red-500">{addrErrors.address}</p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
            Pincode
          </label>
          <input
            name="pincode"
            value={addrForm.pincode}
            onChange={handleAddrChange}
            onBlur={handleAddrBlur}
            className={`${addrInputClass("pincode")} ${addrForm.pincode.length === 6 && pincodeStatus === "serviceable" ? "border-emerald-400 focus:border-emerald-400" : ""} ${addrForm.pincode.length === 6 && pincodeStatus === "not_serviceable" ? "border-red-400 focus:border-red-400" : ""}`}
            placeholder="411001"
          />
          {addrErrors.pincode && addrTouched.pincode && (
            <p className="mt-1 text-xs text-red-500">
              {addrErrors.pincode}
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
            City {pincodeCityState.city && !addrTouched.city ? "(Auto)" : "*"}
          </label>
          <input
            name="city"
            value={addrForm.city}
            onChange={handleAddrChange}
            onBlur={handleAddrBlur}
            className={`${addrInputClass("city")} ${pincodeCityState.city && !addrTouched.city ? "bg-emerald-50 border-emerald-200" : ""}`}
            placeholder="Pune"
          />
          {addrErrors.city && addrTouched.city && (
            <p className="mt-1 text-xs text-red-500">{addrErrors.city}</p>
          )}
          {pincodeCityState.city && !addrTouched.city && (
            <p className="mt-0.5 text-xs text-emerald-600">Auto-filled from pincode</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-gray-500">
            State {pincodeCityState.state && !addrTouched.state ? "(Auto)" : "*"}
          </label>
          <input
            name="state"
            value={addrForm.state}
            onChange={handleAddrChange}
            onBlur={handleAddrBlur}
            className={`${addrInputClass("state")} ${pincodeCityState.state && !addrTouched.state ? "bg-emerald-50 border-emerald-200" : ""}`}
            placeholder="Maharashtra"
          />
          {addrErrors.state && addrTouched.state && (
            <p className="mt-1 text-xs text-red-500">{addrErrors.state}</p>
          )}
          {pincodeCityState.state && !addrTouched.state && (
            <p className="mt-0.5 text-xs text-emerald-600">Auto-filled from pincode</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-2 block text-sm font-semibold text-gray-700">
            Latitude <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <input
            name="latitude"
            type="number"
            step="any"
            value={addrForm.latitude}
            onChange={handleAddrChange}
            onBlur={handleAddrBlur}
            className={addrInputClass("latitude")}
            placeholder="18.5204"
          />
          {addrErrors.latitude && addrTouched.latitude && (
            <p className="mt-1 text-xs text-red-500">
              {addrErrors.latitude}
            </p>
          )}
        </div>
        <div>
          <label className="mb-2 block text-sm font-semibold text-gray-700">
            Longitude <span className="text-gray-400 font-normal">(Optional)</span>
          </label>
          <input
            name="longitude"
            type="number"
            step="any"
            value={addrForm.longitude}
            onChange={handleAddrChange}
            onBlur={handleAddrBlur}
            className={addrInputClass("longitude")}
            placeholder="73.8567"
          />
          {addrErrors.longitude && addrTouched.longitude && (
            <p className="mt-1 text-xs text-red-500">
              {addrErrors.longitude}
            </p>
          )}
        </div>
      </div>

      {/* Pincode status inside new address form */}
      <PincodeStatus status={pincodeStatus} message={pincodeMessage} />

      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        <input
          name="is_default"
          type="checkbox"
          checked={addrForm.is_default}
          onChange={handleAddrChange}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        Make default address
      </label>

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          disabled={addresses_loading}
          className="w-full rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-bold text-gray-600 transition hover:border-gray-300 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
      )}

      <button
        type="submit"
        disabled={
          addresses_loading ||
          pincodeStatus === "not_serviceable" ||
          pincodeStatus === "checking"
        }
        className="w-full rounded-xl bg-primary-600 py-2.5 text-sm font-bold text-white transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {addresses_loading
          ? "Saving..."
          : pincodeStatus === "checking"
            ? "Verifying pincode…"
            : "Save & Use This Address"}
      </button>
    </form>
  );
};

export default AddressForm;
